// Per-tour / per-event accent color, shared by the Event Calendar and the Tour
// Timeline.
//
// WHY THIS FILE EXISTS
// The two pages are documented to use the same color for the same tour, so a
// producer can glance between them and see one trip. That promise was kept by
// copy-pasting an FNV-1a hash into both files, which meant it held only as long as
// nobody edited one copy. Changing the palette broke it immediately. One module,
// one palette, and the promise is structural instead of remembered.
//
// (VehicleSchedule/grid-constants.ts still has its own hashHue. It is keyed on
// VEHICLE id, not tour id — a different keyspace with nothing to match — so it is
// deliberately left alone rather than folded in here.)
//
// WHY A FIXED PALETTE INSTEAD OF A GENERATED HUE
// This used to hash into a 265-degree hue band and render it at 78% lightness
// (dark) / 90% (day). Spreading a hash across that much hue inevitably lands on
// mint, lilac and peach, and pastel lightness finished the job — which is what
// "too eastery" meant. A curated set cannot drift there. The colours are the
// category family (components/ui/categoryColors.ts): lanes and rings take the
// family's mid-tone mark, which keeps the calendar reading as colour, and bar text
// takes its ink, which clears the contrast floor.
//
// RED AND AMBER ARE DELIBERATELY ABSENT. Status owns them: cancelled bars are red,
// pending bars are amber. An accent in those hues makes a bar ambiguous at a
// glance, which is the one thing this color is for. Google's own palette has
// Tomato and Banana; we skip them for that reason, not by oversight.

import { CATEGORY, alpha } from '../components/ui/categoryColors';
import type { CategoryColor } from '../components/ui/categoryColors';

export interface EventAccent {
  /** Kept for callers that want the raw hue (bar tints, dashed lines). */
  hue: number;
  text: string;
  border: string;
  laneBg: string;
  laneBgHL: string;
  laneBgDim: string;
  laneRing: string;
  laneRingHL: string;
  laneRingDim: string;
}

/**
 * The family colours a tour can wear, well separated around the wheel, red and amber
 * left out (above). Peacock sits on cyan and blueberry on purple: at matched
 * lightness, sky and indigo land on top of cobalt.
 *
 * Order matters only for stability — appending is safe, reordering or removing
 * repaints every existing tour.
 */
const ACCENTS: { name: string; colour: CategoryColor }[] = [
  { name: 'basil', colour: CATEGORY.green },
  { name: 'teal', colour: CATEGORY.teal },
  { name: 'peacock', colour: CATEGORY.cyan },
  { name: 'cobalt', colour: CATEGORY.blue },
  { name: 'blueberry', colour: CATEGORY.purple },
  { name: 'grape', colour: CATEGORY.fuchsia },
  { name: 'mulberry', colour: CATEGORY.pink }, // stops short of red
  { name: 'graphite', colour: CATEGORY.neutral }, // reads as "no color" on purpose
];

// A grey has no hue. 220 keeps graphite's HSL consumers (day mode, and the Tour
// Timeline, which adds 10 points of saturation to its text) leaning cool rather than red.
const GREY_HUE = 220;

/** The mark's HSL hue and saturation, for callers that build their own HSL at their own lightness. */
const hslOf = (hex: string): { h: number; s: number } => {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return { h: GREY_HUE, s: 0 };
  const l = (max + min) / 2;
  const sector = max === r ? ((g - b) / d + 6) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: Math.round(sector * 60), s: Math.round((d / (1 - Math.abs(2 * l - 1))) * 100) };
};

const HSL = ACCENTS.map((a) => hslOf(a.colour.mark.hex));

/**
 * FNV-1a 32-bit, then an avalanche fold, then the bucket.
 *
 * THE FOLD IS LOAD-BEARING, do not drop it as noise. `h % 8` samples only the low
 * three bits, and FNV-1a's low bits are a closed chain — multiply-mod-8 depends
 * only on the operand mod 8 — so they mix far more weakly than the full word. The
 * old code got away with it because `% 265` sampled the whole value.
 *
 * Measured on the 22 tours in prod:
 *   h % 8            → 6 of 8 colors ever used, two slots holding 7 tours each
 *   (h ^ h>>>16) % 8 → all 8 used, and the worst slot drops from 7 to 7→4
 *
 * Growing the palette does NOT help at this scale (xor-fold % 12 used only 7 of
 * 12) — the bias was the sampling, not the number of colors.
 *
 * Stable across sessions and machines, which a tour color must be. Changing this
 * function repaints every tour: cheap now, expensive once people have learned them.
 */
const hashIndex = (str: string): number => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  h ^= h >>> 16;
  return (h >>> 0) % ACCENTS.length;
};

/**
 * Accent for a seed (a tour id, or an event id when the event has no tour).
 *
 * Dark mode is the family as it stands: ink text, mark lanes and rings at alpha.
 * Day mode builds HSL from the mark's hue and saturation; its lightness values are
 * tuned, not arbitrary: the lane wash sits at 88% rather than 90% with more
 * saturation so it looks deliberate rather than washed out, and stays light enough
 * for dark bar text to sit on it.
 */
export const accentForSeed = (seed: string | null | undefined, dayMode: boolean): EventAccent | null => {
  if (!seed) return null;
  const i = hashIndex(seed);
  const { h, s } = HSL[i];
  const { ink, mark } = ACCENTS[i].colour;

  return dayMode
    ? {
        hue: h,
        text: `hsl(${h} ${s}% 26%)`,
        border: `hsl(${h} ${s}% 45% / 0.55)`,
        laneBg: `hsl(${h} ${Math.min(s + 8, 90)}% 88%)`,
        laneBgHL: `hsl(${h} ${Math.min(s + 12, 92)}% 79%)`,
        laneBgDim: `hsl(${h} ${s}% 94%)`,
        laneRing: `hsl(${h} ${s}% 50% / 0.5)`,
        laneRingHL: `hsl(${h} ${s}% 40% / 0.9)`,
        laneRingDim: `hsl(${h} ${s}% 72% / 0.3)`,
      }
    : {
        hue: h,
        text: ink.oklch,
        border: alpha(mark, 0.5),
        laneBg: alpha(mark, 0.13),
        laneBgHL: alpha(mark, 0.26),
        laneBgDim: alpha(mark, 0.05),
        laneRing: alpha(mark, 0.42),
        laneRingHL: alpha(mark, 0.72),
        laneRingDim: alpha(mark, 0.16),
      };
};

/**
 * Just the hue + saturation for a seed, for callers that build their own HSL at
 * their own lightness and alpha (the Tour Timeline's bars and transit wings). Read
 * off the same mark the calendar draws, so the two pages give a tour one hue.
 *
 * Saturation is returned alongside the hue ON PURPOSE. A hue-only contract worked
 * when every color sat at one saturation. This palette varies it (graphite is 0%),
 * so a hue-only caller would render the gray as a blue-gray and quietly stop
 * matching the calendar.
 */
export const accentHslForSeed = (seed: string | null | undefined): { h: number; s: number } | null => {
  if (!seed) return null;
  const { h, s } = HSL[hashIndex(seed)];
  return { h, s };
};

/** Convenience for the calendar's event shape. tour_id wins so tour members clump. */
export const accentForEvent = (
  event: { tour_id?: string | null; id?: string | null },
  dayMode: boolean
): EventAccent | null => accentForSeed(event.tour_id || event.id, dayMode);
